/**
 * care-api - typed wrappers over the Member Care API, built on the kept apiClient spine.
 *
 * Reads use apiJson (throws on non-2xx). Mutations use apiPost, which surfaces { ok, status, data }
 * WITHOUT throwing, so callers can detect a 409 stale-eTag conflict (optimistic concurrency) and show
 * the ClariCommand "this changed, reload" banner instead of silently losing a write.
 *
 * Multi-client since 2026-09-03: every call carries the operator's selected client (see
 * @lib/clients). `currentClient()` is re-exported here so existing call sites keep one import.
 */
import type { PostalFactsView } from '@lib/plain-rows';
import { apiJson, apiFetch, apiPost, type PostResult } from '@lib/apiClient';
import type {
  ActionType,
  AnswerChangedFlag,
  ArchivedEventsResp,
  CompletedElsewhereResp,
  ContactEntry,
  Direction,
  EventAttachment,
  EventRecord,
  EventSource,
  EventStatus,
  EventTag,
  EnrouteKitRow,
  EnrouteListResp,
  Faxability,
  LaterRefusalFlag,
  RequestType,
  ListResp,
  MatchCandidateInfo,
  MatchConfidence,
  MatchedBy,
  MemberBrowseRow,
  MemberCommsResp,
  CareMarksResp,
  MemberDetailResp,
  MemberListResp,
  MemberResultsResp,
  MemberSearchResp,
  MemberSnapshot,
  PcpDetailResp,
  PcpListResp,
  PcpSearchResp,
  PendingPress,
  MatchSuggestion,
  EmailView,
  ReplyActionOutcome,
  ShippingListResp,
  ShippingRow,
  ResponseChannel,
  ResultsListResp,
  ResultsRow,
  ReviewBatchesResp,
  ReviewBatchResp,
  ReviewBatchSortKey,
  SortDir,
  RosterResp,
  ShippingDetailResp,
  ShippingSearchResp,
  SmsConversationResp,
  SmsInboundResp,
  SmsSendResp,
  HeldSmsListResp,
  EmailReplyRow,
} from '@lib/care-model';
export type { EmailReplyRow } from '@lib/care-model';

import { currentClient } from '@lib/clients';
import { currentScope } from '@lib/useSessionReason';

/** The client every request below is made for (the operator's picker selection). */
export { currentClient };

/**
 * W9: the stop check before a call (POST /api/care/call-check). A plain yes is `callable: true`; a no carries the
 * one sentence to show. lib/call-guard.ts reads anything else as a no (fail closed).
 *
 * BOUND TO THE CASE (T-1010-99-CALLBIND, 1:46 PM 11 Oct 2026): the request names the case and which of its
 * numbers (the entry's from-phone, or the matched member's first or second roster number), NEVER a number. The
 * server reads the number from the case, and a yes carries the number it checked (`phone`, ten digits), which
 * is the number Teams is opened for.
 */
export type CallLine = 'from' | 'phone1' | 'phone2';
export interface CallCheckResp { callable?: boolean; phone?: string; code?: string; said?: string }
export async function callCheck(eventId: string, line: CallLine): Promise<PostResult<CallCheckResp>> {
  return apiPost<CallCheckResp>(`/api/care/call-check`, { client: currentClient(), eventId, line });
}

/**
 * True when an apiPost result is the optimistic-concurrency conflict (someone else changed the row).
 * The rev4 hard claim lock ALSO answers 409, with code 'claimed_by_other' - that one is a different
 * UX (a "Locked to {name}" note, not the reload banner), so it is excluded here; use isLocked().
 */
export function isConflict(r: PostResult<unknown>): boolean {
  const code = (r.data as { code?: string }).code;
  if (code === 'claimed_by_other') return false;
  if (code === 'fax_not_faxable') return false; // the faxable guard's refusal: its own UX, see isNotFaxable()
  // PARTS I, L and M: a plan with no site of its own, and a case whose files would stay in Care, are named
  // refusals said in their own words, never the stale-entry banner.
  if (code === 'receive_only' || code === 'attachments_need_approval') return false;
  if (code && (CAMPAIGN_OPTOUT_REFUSALS as readonly string[]).includes(code)) return false; // M1-C: a named refusal, said in its own words
  return r.status === 409 || code === 'stale_etag';
}

/**
 * M1-C (row 872-5): the refusal codes of the drawer's This campaign only, the screen's copy of
 * CAMPAIGN_OPTOUT_REFUSALS in api/src/lib/campaign-optout.ts, pinned in lockstep by test. Each comes with
 * its own sentence in `error` (not matched, no campaign, already marked, the platform's refusal, ...), so
 * it must reach the operator as that sentence and never as the stale-entry banner a bare 409 shows.
 */
export const CAMPAIGN_OPTOUT_REFUSALS = [
  'unmatched', 'no_campaign', 'already_marked', 'mark_unbuildable', 'platform_refused', 'platform_failed', 'care_decision_write_failed',
] as const;

/** True when a recording was refused by the faxable guard (this member's program cannot fax for them). */
export function isNotFaxable(r: PostResult<unknown>): boolean {
  return r.status === 409 && (r.data as { code?: string }).code === 'fax_not_faxable';
}

/** L4: true when a Resend result letter was refused because the member's results are only failed tests. */
export function isLetterNotSendable(r: PostResult<unknown>): boolean {
  return r.status === 409 && (r.data as { code?: string }).code === 'letter_not_sendable';
}

/** True when a mutation was refused by the rev4 hard claim lock (event claimed by another operator). */
export function isLocked(r: PostResult<unknown>): boolean {
  return r.status === 409 && (r.data as { code?: string }).code === 'claimed_by_other';
}

/** The claimant named by a locked 409 body. */
export function lockOwner(r: PostResult<unknown>): string {
  return String((r.data as { assignedTo?: string }).assignedTo ?? '');
}

/* ----------------------------- reads ----------------------------- */

export async function listEvents(opts: {
  direction?: Direction | undefined;
  status?: EventStatus | undefined;
  q?: string | undefined;
  /** Include only this origin (Calling page: 'out-calling'). */
  source?: EventSource | undefined;
  /** Exclude these origins (member queue: 'out-calling', and 'bad-number' outside its own chip). */
  notSource?: EventSource | readonly EventSource[] | undefined;
  /** Drop Print Station fax-failure referrals (member queue default; `faxFailures` still counts them). */
  excludeFaxFailures?: boolean | undefined;
  /** Additionally return `threads` + `threadTotals` (one row per member / PCP / contact). */
  groupBy?: 'thread' | undefined;
} = {}): Promise<ListResp> {
  const p = new URLSearchParams({ client: currentClient() });
  if (opts.direction) p.set('direction', opts.direction);
  if (opts.status) p.set('status', opts.status);
  if (opts.q && opts.q.trim()) p.set('q', opts.q.trim());
  if (opts.source) p.set('source', opts.source);
  const not = typeof opts.notSource === 'string' ? [opts.notSource] : [...(opts.notSource ?? [])];
  if (not.length) p.set('notSource', not.join(','));
  if (opts.excludeFaxFailures) p.set('excludeFaxFailures', '1');
  if (opts.groupBy) p.set('groupBy', opts.groupBy);
  return apiJson<ListResp>(`/api/care/events?${p.toString()}`);
}

/**
 * Row 400 rev2: the ARCHIVED communications of the members on the 01A grid, from member_care.db, read
 * ONCE per set of members on screen (the caller memoises on the id set), never per row. The live
 * (open) other comms come from the queue's own thread grouping.
 */
export async function memberComms(members: string[]): Promise<MemberCommsResp> {
  const r = await apiPost<MemberCommsResp>(`/api/care/member-comms`, { client: currentClient(), members });
  if (!r.ok) throw new Error(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
  return r.data as MemberCommsResp;
}

/**
 * Row 419: the MARKED VIEW's read on the Opt Out and STOP sub-tabs, GET /api/care/care-marks: the
 * archived entries carrying that mark, from the builder's care_marks table (up to ten minutes behind).
 */
export async function careMarks(kind: string, campaign?: string | null, limit?: number): Promise<CareMarksResp> {
  const q = new URLSearchParams({ client: currentClient(), kind });
  if (campaign) q.set('campaign', campaign);
  if (limit) q.set('limit', String(limit));
  return apiJson<CareMarksResp>(`/api/care/care-marks?${q}`);
}

export async function getEvent(id: string): Promise<EventRecord> {
  const r = await apiJson<{ event: EventRecord }>(
    `/api/care/event?client=${currentClient()}&id=${encodeURIComponent(id)}`,
  );
  return r.event;
}

/** PART Q (William, 17 Oct 2026): the whole email of an Email case, read from its raw copy (GET /api/care/event/email). */
export async function getEmailMessage(id: string): Promise<EmailView> {
  const r = await apiJson<{ email: EmailView }>(
    `/api/care/event/email?client=${currentClient()}&id=${encodeURIComponent(id)}`,
  );
  return r.email;
}

export async function getRoster(opts: { q?: string; limit?: number } = {}): Promise<RosterResp> {
  const p = new URLSearchParams({ client: currentClient() });
  if (opts.q && opts.q.trim()) p.set('q', opts.q.trim());
  if (opts.limit) p.set('limit', String(opts.limit));
  return apiJson<RosterResp>(`/api/care/roster/no-consent?${p.toString()}`);
}

/* --------------------- 07 Archive: review-batch bundles (read-only) --------------------- */

/**
 * One page of review-batch bundles (each = one approval package shipped to the Portal), newest first.
 * Paged because the API reads one manifest per bundle; omit opts for the server's default page.
 */
export async function listReviewBatches(
  opts: {
    limit?: number; offset?: number;
    /** One key (with `dir`), or row 356's `k1:asc,k2:desc` from `sortParam`. */
    sort?: ReviewBatchSortKey | string; dir?: SortDir;
    /** Row 356: `slicerParam` of the slicers set. */
    slice?: string;
  } = {},
): Promise<ReviewBatchesResp> {
  const p = new URLSearchParams({ client: currentClient() });
  if (opts.limit != null) p.set('limit', String(opts.limit));
  if (opts.offset) p.set('offset', String(opts.offset));
  if (opts.sort) p.set('sort', opts.sort);
  if (opts.dir) p.set('dir', opts.dir);
  if (opts.slice) p.set('slice', opts.slice);
  return apiJson<ReviewBatchesResp>(`/api/care/review-batches?${p.toString()}`);
}

/** One bundle's manifest rows (members, disposition actions, resolution timing, Portal handoff ref). */
export async function getReviewBatch(batchId: string): Promise<ReviewBatchResp> {
  return apiJson<ReviewBatchResp>(
    `/api/care/review-batch?client=${currentClient()}&id=${encodeURIComponent(batchId)}`,
  );
}

/* --------------------- 08 Archive: closed entries (R. 82 / row 401, read-only) --------------------- */

/**
 * One page of closed entries from member_care.db (every entry in a terminal status), the queue's
 * own PHI-light row plus who closed it and when. Search, status, date range, sort and paging all
 * run on the server over the whole table, because the screen holds one page.
 */
export async function listArchivedEvents(
  opts: {
    q?: string; limit?: number; offset?: number;
    /** Row 356's `k1:asc,k2:desc` from `sortParam`. */
    sort?: string;
    /** Row 356: `slicerParam` of the slicers set (status). */
    slice?: string;
    /** ISO instants (the operator's local day, opened and closed), or blank. */
    from?: string; to?: string;
  } = {},
): Promise<ArchivedEventsResp> {
  const p = new URLSearchParams({ client: currentClient() });
  if (opts.q && opts.q.trim()) p.set('q', opts.q.trim());
  if (opts.limit != null) p.set('limit', String(opts.limit));
  if (opts.offset) p.set('offset', String(opts.offset));
  if (opts.sort) p.set('sort', opts.sort);
  if (opts.slice) p.set('slice', opts.slice);
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  return apiJson<ArchivedEventsResp>(`/api/care/archived-events?${p.toString()}`);
}

/**
 * Session-scoped blob-URL cache for streamed media (audio / attachments). The WIRE stays
 * Cache-Control: no-store (PHI must never land in the browser's disk cache - the deliberate rev4
 * posture); this in-MEMORY cache only spares the re-download on drawer re-opens and replays within
 * one session (rev7, William: audio load times). The cache OWNS the object URLs - callers must NOT
 * revoke them; eviction (insertion-order LRU, cap 40) revokes.
 */
const streamCache = new Map<string, string>();
const STREAM_CACHE_MAX = 40;
/** In-flight dedupe: concurrent same-key fetches share ONE promise (a lost race would otherwise
 * mint a second blob URL and revoke the one already mounted in an <audio>/<img> - rev7 panel). */
const streamInFlight = new Map<string, Promise<string | null>>();
/** Evicted/replaced URLs revoke on a DELAY: an <audio> element may still be playing one. Ten
 * minutes comfortably outlives any voicemail; the memory overlap is a handful of blobs. */
const STREAM_REVOKE_DELAY_MS = 10 * 60 * 1000;

function revokeLater(url: string): void {
  setTimeout(() => URL.revokeObjectURL(url), STREAM_REVOKE_DELAY_MS);
}

function streamCacheGet(key: string): string | null {
  const hit = streamCache.get(key);
  if (!hit) return null;
  streamCache.delete(key); // re-insert to refresh recency
  streamCache.set(key, hit);
  return hit;
}

function streamCachePut(key: string, url: string): void {
  const prior = streamCache.get(key);
  if (prior) {
    revokeLater(prior);
    streamCache.delete(key);
  }
  streamCache.set(key, url);
  if (streamCache.size > STREAM_CACHE_MAX) {
    const oldest = streamCache.keys().next().value as string;
    revokeLater(streamCache.get(oldest)!);
    streamCache.delete(oldest);
  }
}

/** Test/ops hook: drop every cached media blob (e.g. on logout). */
export function clearStreamCache(): void {
  for (const url of streamCache.values()) URL.revokeObjectURL(url);
  streamCache.clear();
  streamInFlight.clear();
}

/** One shared fetch per key; the resolved URL lands in the cache exactly once. */
function streamFetchShared(key: string, doFetch: () => Promise<string | null>): Promise<string | null> {
  const cached = streamCacheGet(key);
  if (cached) return Promise.resolve(cached);
  const inflight = streamInFlight.get(key);
  if (inflight) return inflight;
  const p = doFetch()
    .then((url) => {
      if (url) streamCachePut(key, url);
      return url;
    })
    .finally(() => {
      streamInFlight.delete(key);
    });
  streamInFlight.set(key, p);
  return p;
}

/** Fetch a binary stream (audio/transcript) as a CACHE-OWNED blob URL (do not revoke). */
export function fetchStreamUrl(path: 'audio' | 'transcript', id: string): Promise<string | null> {
  return streamFetchShared(`${currentClient()}|${path}|${id}`, async () => {
    const res = await apiFetch(`/api/care/event/${path}?client=${currentClient()}&id=${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  });
}

/** Fetch a transcript (.vtt/.txt) as text through the bearer-bearing apiFetch. */
export async function fetchTranscriptText(id: string): Promise<string | null> {
  const res = await apiFetch(`/api/care/event/transcript?client=${currentClient()}&id=${encodeURIComponent(id)}`);
  if (!res.ok) return null;
  return res.text();
}

/* --------------------------- mutations --------------------------- */
/* Each returns the raw PostResult so the caller can branch on status (409 conflict, 422 validation). */

export function claimEvent(id: string, release = false): Promise<PostResult<{
  eventId: string;
  assignedTo: string;
  operatorName: string;
  status: EventStatus;
}>> {
  return apiPost(`/api/care/event/claim`, { client: currentClient(), id, release });
}

/**
 * Transfer: hand the case to another Care team member (William, 2 Oct 2026: "Transfer just has a
 * drop down to another team member. That is all."). `to` is the member's ADDRESS, the key of a
 * choice from useTeam() (lib/transfer-targets.ts); the first build's keys 'alex' and 'sherry' are
 * still accepted. The same claim route; the server checks the address against the active team and
 * refuses anyone else (400 transfer_target_unknown), yourself (400 transfer_to_self: that is a
 * Claim), a finished case (409 terminal_status), or answers 503 team_unavailable when it cannot read
 * the team. On success the new holder is emailed the case link; `notified` says whether that email
 * went (best-effort: false never undoes the transfer; `notifyError` says why). Unclaim is
 * claimEvent(id, true), which puts the case back in the main queue.
 */
export function transferEvent(id: string, to: string): Promise<PostResult<{
  eventId: string;
  assignedTo: string;
  operatorName: string;
  status: EventStatus;
  notified: boolean;
  notifyError?: string;
}>> {
  return apiPost(`/api/care/event/claim`, { client: currentClient(), id, transferTo: to });
}

export function setPhone(id: string, phone: string): Promise<PostResult<{
  eventId: string;
  fromPhone: string;
  phoneSource: 'manual';
}>> {
  return apiPost(`/api/care/event/phone`, { client: currentClient(), id, phone });
}

export function matchOne(id: string, memberId?: string, campaign?: string | null): Promise<PostResult<{
  eventId: string;
  /** R. 81: the campaign roster the match read (the entry's own, else the reference). */
  campaign?: string | null;
  memberId: string;
  matchedBy: MatchedBy;
  matchConfidence: MatchConfidence;
  matchCandidates: string[];
  matchCandidateInfo: MatchCandidateInfo[];
  memberSnapshot: MemberSnapshot | null;
}>> {
  const body: Record<string, unknown> = { client: currentClient(), id };
  if (memberId) body.memberId = memberId;
  // The roster the member was picked from, set on the entry in the same press (only a candidate on
  // exactly one current roster, on an entry that names no campaign; see the drawer).
  if (memberId && campaign) body.campaign = campaign;
  return apiPost(`/api/care/event/match`, body);
}

/** Unmatch (William feedback #3, 2026-07-14): remove an incorrect match WITHOUT a replacement -
 * the event returns to Unmatched and the contradicted known-contact binding is deactivated. */
export function matchClear(id: string): Promise<PostResult<{
  eventId: string;
  memberId: string;
  matchedBy: MatchedBy;
  matchConfidence: MatchConfidence;
  matchCandidates: string[];
  matchCandidateInfo: MatchCandidateInfo[];
  memberSnapshot: MemberSnapshot | null;
}>> {
  return apiPost(`/api/care/event/match`, { client: currentClient(), id, clear: true });
}

export function matchAll(): Promise<PostResult<{
  client: string;
  matched: number;
  ambiguous: number;
  unmatched: number;
  /** Events the sweep left alone: conflicted mid-sweep, freshly claimed/cleared/manual, or gone. */
  skipped: number;
  /** PART P: entries left without a member that now carry a suggestion (absent from an older server). */
  suggested?: number;
  /** R. 81: entries swept per campaign roster read (the entry's own, else the client's own; '' = none). */
  campaigns?: Record<string, number>;
}>> {
  return apiPost(`/api/care/event/match`, { client: currentClient(), all: true });
}

export function respond(id: string, responseText: string, channel: ResponseChannel): Promise<PostResult<{
  eventId: string;
  status: EventStatus;
  channel: ResponseChannel;
  sent: boolean;
  sendError?: string;
}>> {
  return apiPost(`/api/care/event/respond`, { client: currentClient(), id, responseText, channel });
}

export function saveNotes(
  id: string,
  fields: { notes?: string; portalNote?: string; status?: EventStatus; followUp?: boolean },
): Promise<PostResult<{ eventId: string; status: EventStatus; followUp: boolean }>> {
  const body: Record<string, unknown> = { client: currentClient(), id };
  if (fields.notes !== undefined) body.notes = fields.notes;
  if (fields.portalNote !== undefined) body.portalNote = fields.portalNote;
  if (fields.status !== undefined) body.status = fields.status;
  if (fields.followUp !== undefined) body.followUp = fields.followUp;
  return apiPost(`/api/care/event/notes`, body);
}

/**
 * Close file (William, 2 Oct 2026: "add an option to say close file (log this in db and then also push
 * to closure ...)"): logs the close on the case and closes it. Nothing is sent to the member. `note`
 * is the short note on the close's row; `notes` is the running notes box, saved with it.
 *
 * Wave 3 item 4: with the deployment's MEMBERCARE_STAGED_PRESSES on, the answer says `staged: true`:
 * the close was QUEUED on the case (it moves to review) and the case closes when it is approved. The
 * screen reads `staged` off the answer and never words a queued close as closed.
 */
export function closeFile(
  id: string,
  fields: { note?: string; notes?: string },
): Promise<PostResult<{ eventId: string; status: EventStatus; staged: boolean }>> {
  const body: Record<string, unknown> = { client: currentClient(), id };
  if (fields.note !== undefined) body.note = fields.note;
  if (fields.notes !== undefined) body.notes = fields.notes;
  return apiPost(`/api/care/event/close-file`, body);
}

/**
 * Wave 3 item 4: take ONE queued press off a case before it is approved (POST /api/care/event/pending
 * with `withdraw: true`). The press then never takes effect. Claim-locked like every drawer edit; while
 * the case is in review the server refuses (409 in_review: the reviewer returns it first, or the
 * operator recalls it). Sends nothing to anyone.
 */
/**
 * PART B (Find Member, William 16 Oct 2026): queue the match and its decision for approval. Nothing is written
 * to the member until the case is approved.
 *
 * Console faults 1 and 2 (27 Oct 2026): `send: true` also sends the case to the Approval Queue in the same server
 * write (the answer says `sentToReview: true`), so a match is never left in_progress by a send that never ran. Sent
 * with keepalive, so a request already on its way finishes if the page is left.
 */
export function matchMemberForApproval(
  id: string,
  body: { memberId: string; campaign: string | null; decision: string; reason: string; send?: boolean },
): Promise<PostResult<{ eventId: string; staged?: boolean; sentToReview?: boolean; pendingId?: string; memberName?: string; message?: string; error?: string; code?: string }>> {
  return apiPost(`/api/care/event/match-member`, { client: currentClient(), id, ...body, ...(body.campaign ? {} : { campaign: null }) }, { keepalive: true });
}

/**
 * PART P (William, 17 Oct 2026): run the suggestion pass on one case (the drawer asks when an unmatched case with
 * no suggestion is opened), or, with `dismiss`, "Not this member" (PART R). Writes only the suggestion; never a match.
 */
export function suggestMatch(
  id: string,
  dismiss?: string,
): Promise<PostResult<{ eventId: string; written: boolean; matchSuggestion: MatchSuggestion | null; error?: string }>> {
  return apiPost(`/api/care/event/suggest`, { client: currentClient(), id, ...(dismiss ? { dismiss } : {}) });
}

/**
 * PART S2 (William, 17 Oct 2026: "so I can both match the member (confirm identity) and the specific task"): change the
 * decision of the Find Member match already waiting on a case (the queue's Opt-Out or Stop). Nothing changes until the
 * case is approved.
 */
export function amendMatchDecision(
  id: string,
  decision: 'opt-out-campaign' | 'opt-out-all' | 'stop' | 'none',
  reason: string,
  send?: boolean,
): Promise<PostResult<{ eventId: string; staged?: boolean; amended?: boolean; sentToReview?: boolean; memberName?: string; message?: string; error?: string; code?: string }>> {
  return apiPost(`/api/care/event/match-member`, { client: currentClient(), id, decision, reason, amend: true, ...(send ? { send: true } : {}) }, { keepalive: true });
}

/**
 * I2 a (William, 17 Oct 2026): what the withdraw did with each texting block the withdrawn press had put on (the
 * server's api/src/lib/stop-approval.ts WithdrawnBlock). Absent when the press had put none on.
 */
export interface WithdrawnStop {
  blockId: string;
  kind: string;
  result: 'lifted' | 'nothing-to-lift' | 'kept' | 'failed';
  code: string;
  outcome?: string;
  heldFor?: string;
}

export function withdrawPending(
  id: string,
  pendingId: string,
): Promise<PostResult<{ eventId: string; status?: EventStatus; pending?: PendingPress[]; stops?: WithdrawnStop[] }>> {
  return apiPost(`/api/care/event/pending`, { client: currentClient(), id, pendingId, withdraw: true });
}

/** One action in a rev4 multi-select record call. `data` is the STRUCTURED per-type payload
 * (confirmed Portal contract 4b.2); `detail` is human-only free text. */
export interface ActionInput {
  type: ActionType;
  detail?: string;
  data?: Record<string, unknown>;
}

/** rev4 multi-action record (William ruling #1): every checked action in ONE call, no handoff emit. */
export function recordActions(
  id: string,
  actions: ActionInput[],
): Promise<PostResult<{ eventId: string; recorded: number; status: EventStatus; directWrite?: DirectWriteOutcome[] }>> {
  return apiPost(`/api/care/event/action`, { client: currentClient(), id, actions });
}

/** Row 217(b): the operator chooses the event's campaign ('' clears it). Claim-locked like every drawer edit. */
export function setEventCampaign(
  id: string,
  campaign: string,
): Promise<PostResult<{ eventId: string; campaign: string | null; campaignSource: string | null; campaignBasis: string }>> {
  return apiPost(`/api/care/event/campaign`, { client: currentClient(), id, campaign });
}

/**
 * Row 216: the outcome of the direct write to the care decisions record, as the API reports it on a
 * review decision and on a drawer opt-out / opt-in. `enabled` false is the deployed state (switch
 * off). The platform half reports ClariConnect's master do-not-text list in its own words.
 */
export interface PlatformOutcome {
  available: boolean;
  attempted: boolean;
  reason: string;
  status?: 'done' | 'refused' | 'conflict' | 'invalid' | 'unknown';
  verb?: string;
  channel?: string;
  clientRequestId?: string;
  platformStatus?: string;
  carrierStillOptedOut?: boolean | null;
  auditStored?: string;
}

export interface DirectWriteOutcome {
  enabled: boolean;
  record: { written: boolean; file: string; error: string } | null;
  platform: PlatformOutcome;
  skipped: string;
}

/** The texting platform's half in one clause. '' when nothing was attempted for a reason not worth saying. */
export function platformSentence(p: PlatformOutcome): string {
  if (!p.attempted) return p.reason ? `texting platform: nothing sent (${p.reason})` : '';
  switch (p.status) {
    case 'done':
      return `texting platform: ${p.platformStatus || 'done'} (${p.channel})`
        + (p.carrierStillOptedOut
          ? '. The carrier still holds this number opted out, so texts will bounce until the member texts START to 833-286-9574'
          : '');
    case 'refused': return `texting platform: refused, nothing written there (${p.reason})`;
    case 'conflict': return `texting platform: NOT written, the row changed under it (${p.reason}); decide again`;
    case 'invalid': return `texting platform: NOT written (${p.reason})`;
    case 'unknown': return `texting platform: outcome UNKNOWN (${p.reason}); decide again to retry the same request, it will not apply twice`;
    default: return '';
  }
}

/** True when the platform half needs the operator's attention: nothing written that should have been, or unknown. */
export function platformNeedsAttention(p: PlatformOutcome | undefined): boolean {
  return !!p && p.attempted && (p.status === 'conflict' || p.status === 'invalid' || p.status === 'unknown');
}

/** One sentence for the operator, or '' when the switch is off (nothing to say). */
export function directWriteSentence(d: DirectWriteOutcome | undefined): string {
  if (!d || !d.enabled) return '';
  const plat = platformSentence(d.platform);
  if (d.record && !d.record.written) return `Recorded here, but the opt-out record was NOT written: ${d.record.error}${plat ? `; ${plat}.` : ''}`;
  if (platformNeedsAttention(d.platform)) return `Recorded here, but ${plat}.`;
  if (d.record?.written) return `Opt-out record: written${plat ? `; ${plat}.` : '.'}`;
  return d.skipped ? `Opt-out record: nothing to write (${d.skipped}).` : '';
}

/** Single-action convenience (the Flow-B consent shortcuts); rides the same multi-action route. */
export function recordAction(
  id: string,
  type: ActionType,
  detail?: string,
  data?: Record<string, unknown>,
): Promise<PostResult<{ eventId: string; recorded: number; status: EventStatus }>> {
  return recordActions(id, [{ type, ...(detail !== undefined ? { detail } : {}), ...(data !== undefined ? { data } : {}) }]);
}

/** rev4 contact log (replaces the respond UX): Marny logs the call/email/SMS she made herself. */
export function logContact(
  id: string,
  channel: ContactEntry['channel'],
  note: string,
  recordingRef?: string,
): Promise<PostResult<{ eventId: string; entry: ContactEntry; contactCount: number }>> {
  const body: Record<string, unknown> = { client: currentClient(), id, channel, note };
  if (recordingRef) body.recordingRef = recordingRef;
  return apiPost(`/api/care/event/contact`, body);
}

/** One item-6 request input (what the member said/asked). Internal capture; never rides the envelope. */
export interface RequestInput {
  type: RequestType;
  note?: string;
}

/**
 * Item 6 "Requests" (William 2026-07-08): REPLACE the captured request set for this event. Distinct
 * from recordActions (item-12 disposition); requests are internal only and never ride the Portal
 * resolution. Pass the full checked set each save (an empty array clears them).
 */
export function recordRequests(
  id: string,
  requests: RequestInput[],
): Promise<PostResult<{ eventId: string; requestCount: number }>> {
  return apiPost(`/api/care/event/request`, { client: currentClient(), id, requests });
}

/** rev4 attachment upload (multipart). No Content-Type header: the browser sets the boundary. */
export async function uploadAttachment(
  id: string,
  kind: EventAttachment['kind'],
  file: File,
): Promise<PostResult<{ eventId: string; attachment: EventAttachment; attachmentCount: number }>> {
  const form = new FormData();
  form.set('client', currentClient());
  form.set('id', id);
  form.set('kind', kind);
  form.set('file', file);
  const res = await apiFetch(`/api/care/event/attachment`, { method: 'POST', body: form });
  let data: Record<string, unknown>;
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data: data as never };
}

/** Fetch one attachment binary as a blob object URL (audited PHI view; caller revokes the URL). */
/** Fetch an attachment as a CACHE-OWNED blob URL (do not revoke); same cache as fetchStreamUrl. */
export function fetchAttachmentUrl(id: string, ref: string): Promise<string | null> {
  return streamFetchShared(`att|${id}|${ref}`, async () => {
    const p = new URLSearchParams({ client: currentClient(), id, ref });
    const res = await apiFetch(`/api/care/event/attachment?${p.toString()}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  });
}

/** rev4 light member/PCP tag. */
export function tagEvent(
  id: string,
  tag: string,
  entity: EventTag['entity'],
): Promise<PostResult<{ eventId: string; tags: EventTag[] }>> {
  return apiPost(`/api/care/event/tag`, { client: currentClient(), id, tag, entity });
}

export type ReviewVerb = 'send' | 'approve' | 'return' | 'recall' | 'archive';

/** rev4 review verbs: send (drawer, claim-locked) / approve (reviewer) / return (reviewer). */
export function reviewEvent(
  id: string,
  verb: ReviewVerb,
  note?: string,
): Promise<PostResult<{
  eventId: string;
  status: EventStatus;
  clearedTs: string;
  reviewedBy: string;
  sentToReviewTs: string;
  approvedTs: string;
  handoff: { type: string; ref: string } | null;
  /** Wave 3 item 4 (approve): what the approval did with each press queued on the case; absent when none was. */
  pending?: AppliedPressAnswer[];
  /** STOP path (approve, MEMBERCARE_OPTOUT_EVERYWHERE on): the envelope revision for a late STOP; QA-1689 F2 reads it. */
  optOutEverywhere?: { envelope?: { reemitted: boolean; actions: number; ref?: string; error?: string } };
}>> {
  const body: Record<string, unknown> = { client: currentClient(), id, verb };
  if (note !== undefined) body.note = note;
  // Console fault 2 (27 Oct 2026): a send already on its way finishes if the page is left. Not for a long note: the
  // browser refuses a keepalive body over 64 KB.
  return apiPost(`/api/care/event/review`, body, verb === 'send' && (note ?? '').length < 8000 ? { keepalive: true } : undefined);
}

/** rev4 batch approval -> the dated station archive (Printing pattern). */
export function approveBatch(
  ids: string[],
  note?: string,
): Promise<PostResult<{
  approved: string[];
  failed: { id: string; error: string }[];
  /** Phase-2 (batchId stamp / station archive) issues on ALREADY-approved events: the resolutions
   * shipped; these are bookkeeping degradations, reported so the reviewer sees them. */
  stampFailed: { id: string; error: string }[];
  batchId: string;
  manifestRef: string;
  /** Wave 3 item 4: what the approvals did with the presses queued on those cases, by case; absent when none was. */
  pending?: AppliedPressAnswer[];
  /** Console fault 3 (27 Oct 2026): the ids the server did not start before its time limit; send them again. */
  notStarted?: string[];
}>> {
  const body: Record<string, unknown> = { client: currentClient(), ids };
  if (note !== undefined) body.note = note;
  return apiPost(`/api/care/review/approve-batch`, body);
}

/**
 * rev4 member typeahead (thin audit, no reason gate - the reveal carries the reason). R. 81: `campaign`
 * names the roster to search (the drawer passes the entry's own); absent searches the reference.
 */
export async function memberSearch(q: string, limit = 10, campaign?: string | null, currentScope = false): Promise<MemberSearchResp> {
  const p = new URLSearchParams({ client: currentClient(), q, limit: String(limit) });
  if (campaign) p.set('campaign', campaign);
  // The current-campaign search (23 Sep 2026): for an entry that names no campaign, ask for every
  // roster MEMBERCARE_MATCH_CAMPAIGNS lists. The server ignores it while the setting is absent.
  else if (currentScope) p.set('scope', 'current');
  return apiJson<MemberSearchResp>(`/api/care/member-search?${p.toString()}`);
}

/**
 * The faxable guard's courtesy read: whether this member's program can fax anything to the PCP for them.
 * L4 (16 Oct 2026): the answer also says whether a result letter can be resent (`letter`), judged for the
 * entry's campaign when one is named.
 */
export async function memberFaxability(memberId: string, campaign?: string | null): Promise<Faxability> {
  const p = new URLSearchParams({ client: currentClient(), memberId });
  if (campaign) p.set('campaign', campaign);
  return apiJson<Faxability>(`/api/care/member-faxability?${p.toString()}`);
}

/**
 * rev4 reason-gated full-record reveal (>= 4 char reason enforced server-side; 404 body on miss).
 * R. 81: `campaign` names the roster to read (the drawer passes the entry's own); absent reads the
 * reference. An unknown campaign comes back 404 `campaign_unknown`, never another roster.
 */
export function memberDetail(
  memberId: string,
  reason: string,
  eventLimit?: number,
  campaign?: string | null,
): Promise<PostResult<MemberDetailResp>> {
  const body: Record<string, unknown> = { client: currentClient(), memberId, reason, scope: currentScope() };
  if (eventLimit !== undefined) body.eventLimit = eventLimit;
  if (campaign) body.campaign = campaign;
  return apiPost(`/api/care/member-detail`, body);
}

/** PCP-directory typeahead (thin audit, no reason gate - the panel reveal carries the reason). */
export async function pcpSearch(q: string, limit = 25): Promise<PcpSearchResp> {
  const p = new URLSearchParams({ client: currentClient(), q, limit: String(limit) });
  return apiJson<PcpSearchResp>(`/api/care/pcp-search?${p.toString()}`);
}

/**
 * Reason-gated PCP panel reveal (>= 4 char reason enforced server-side, same discipline AND
 * transport as member-detail: POST body, so the free-text reason never rides a URL into platform
 * logs; 404 body on miss). Keyed by NPI, or by exact name for a no-NPI directory row.
 */
export function pcpDetail(
  key: { npi?: string; name?: string },
  reason: string,
): Promise<PostResult<PcpDetailResp>> {
  const body: Record<string, unknown> = { client: currentClient(), reason, scope: currentScope() };
  if (key.npi) body.npi = key.npi;
  else if (key.name) body.name = key.name;
  return apiPost(`/api/care/pcp-detail`, body);
}

/** Shipping typeahead over kit + letter tracking (thin audit; the member reveal carries the reason). */
export async function shippingSearch(q: string, limit = 100): Promise<ShippingSearchResp> {
  const p = new URLSearchParams({ client: currentClient(), q, limit: String(limit) });
  return apiJson<ShippingSearchResp>(`/api/care/shipping-search?${p.toString()}`);
}

/* --------- Browse-all Lookup lists (2026-07-14 rework, Portal parity) --------- */
/* Reason-gated whole-roster reads: one POST per tab open (the reason never rides a URL), the
 * client virtualizes + searches in memory. Each returns the raw PostResult so the section can
 * surface the 400 reason-gate distinctly from a transport failure. */

export function memberList(reason: string, limit?: number): Promise<PostResult<MemberListResp>> {
  const body: Record<string, unknown> = { client: currentClient(), reason, scope: currentScope() };
  if (limit !== undefined) body.limit = limit;
  return apiPost(`/api/care/member-list`, body);
}

/**
 * The ONE call that fetches screening outcomes for a browse screen (QA-113 option 4).
 *
 * It is deliberately impossible to call without a selection: `toResultSelection` builds the body
 * from the rail's own chips, and the API refuses an empty one with a 400 rather than answering for
 * the whole roster. The response is an overlay keyed by member id, merged onto the rows already on
 * screen, so the outcome exists in the browser only for members the operator asked about.
 */
export function memberResults(
  reason: string,
  selection: { resultValue: string[]; resultStatus: string[]; milestone: string[] },
): Promise<PostResult<MemberResultsResp>> {
  return apiPost(`/api/care/member-results`, { client: currentClient(), reason, scope: currentScope(), selection });
}

export function pcpList(reason: string, limit?: number): Promise<PostResult<PcpListResp>> {
  const body: Record<string, unknown> = { client: currentClient(), reason, scope: currentScope() };
  if (limit !== undefined) body.limit = limit;
  return apiPost(`/api/care/pcp-list`, body);
}

export function shippingList(reason: string, limit?: number, kind: 'all' | 'kit' | 'letter' = 'all'): Promise<PostResult<ShippingListResp>> {
  const body: Record<string, unknown> = { client: currentClient(), reason, scope: currentScope(), kind };
  if (limit !== undefined) body.limit = limit;
  return apiPost(`/api/care/shipping-list`, body);
}

/**
 * The roster Excel export (Portal members-list export parity): POSTs the on-screen filtered +
 * sorted member ids with an EXPORT-scoped reason; the server re-reads exactly those members,
 * audits + watermarks, and answers the workbook. Returns the blob + server-named filename, or
 * throws with the HTTP status on failure.
 */
export async function memberListExport(
  reason: string,
  rows: Pick<MemberBrowseRow, 'member_id'>[],
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`/api/care/member-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client: currentClient(),
      reason,
      scope: 'export',
      format: 'xlsx',
      ids: rows.map((r) => ({ member_id: r.member_id })),
    }),
  });
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const filename =
    (res.headers.get('Content-Disposition') ?? '').match(/filename="([^"]+)"/)?.[1] ??
    'members_26fobt.xlsx';
  return { blob, filename };
}

/** The "completed via another method" report (Alignment 2026-08). Audited server-side; thin read. */
export async function completedElsewhereReport(): Promise<CompletedElsewhereResp> {
  const p = new URLSearchParams({ client: currentClient() });
  return apiJson<CompletedElsewhereResp>(`/api/care/report/completed-elsewhere?${p.toString()}`);
}

/** Reason-gated, watermarked Excel export of the report (member-list export discipline). */
export async function completedElsewhereExport(
  reason: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`/api/care/report/completed-elsewhere`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client: currentClient(), reason }),
  });
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const filename =
    (res.headers.get('Content-Disposition') ?? '').match(/filename="([^"]+)"/)?.[1] ??
    'completed_elsewhere.xlsx';
  return { blob, filename };
}

/** Per-kit USPS scan timeline (thin audit; logistics only). Row 418: the client rides the query
 *  string like every other GET (it was missing, so the Shipping scan timeline had been a 400 since
 *  Wave 1 made the client required). */
export async function shippingDetail(barcode: string): Promise<ShippingDetailResp> {
  const p = new URLSearchParams({ client: currentClient(), barcode });
  return apiJson<ShippingDetailResp>(`/api/care/shipping-detail?${p.toString()}`);
}

/* --------- Row 418 (19 Sep 2026): the Lookup like the main portal --------- */
/* The Enroute kits and the Results roster (reason-gated, one POST per tab open), the per-tab exports
 * (exactly the rows on screen, in their order, with the export-scoped reason the page re-prompted
 * for), and the audit beacons. Every body carries the session grant's scope beside the reason. */

async function downloadXlsx(path: string, body: Record<string, unknown>, fallback: string): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client: currentClient(), scope: currentScope(), ...body, format: 'xlsx' }),
  });
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const filename = (res.headers.get('Content-Disposition') ?? '').match(/filename="([^"]+)"/)?.[1] ?? fallback;
  return { blob, filename };
}

export function enrouteList(reason: string, limit?: number): Promise<PostResult<EnrouteListResp>> {
  const body: Record<string, unknown> = { client: currentClient(), reason, scope: currentScope() };
  if (limit !== undefined) body.limit = limit;
  return apiPost(`/api/care/enroute-list`, body);
}

export function enrouteExport(reason: string, rows: Pick<EnrouteKitRow, 'barcode_id'>[]): Promise<{ blob: Blob; filename: string }> {
  return downloadXlsx(`/api/care/enroute-list`, { reason, scope: 'export', barcodes: rows.map((r) => r.barcode_id) }, 'enroute_kits.xlsx');
}

export function resultsList(reason: string, limit?: number): Promise<PostResult<ResultsListResp>> {
  const body: Record<string, unknown> = { client: currentClient(), reason, scope: currentScope() };
  if (limit !== undefined) body.limit = limit;
  return apiPost(`/api/care/results-list`, body);
}

export function resultsExport(reason: string, rows: Pick<ResultsRow, 'member_id'>[]): Promise<{ blob: Blob; filename: string }> {
  return downloadXlsx(`/api/care/results-list`, { reason, scope: 'export', ids: rows.map((r) => ({ member_id: r.member_id })) }, 'results.xlsx');
}

/** The PCP tab's export, keyed the way the list groups (npi:<npi> / name:<name>). */
export function pcpListExport(reason: string, keys: string[]): Promise<{ blob: Blob; filename: string }> {
  return downloadXlsx(`/api/care/pcp-list`, { reason, scope: 'export', keys }, 'pcps.xlsx');
}

/** Enroute's Letters sub-tab export (and the kits through shipping-list, by row key). */
export function shippingListExport(reason: string, kind: 'kit' | 'letter' | 'all', keys: string[]): Promise<{ blob: Blob; filename: string }> {
  return downloadXlsx(`/api/care/shipping-list`, { reason, scope: 'export', kind, keys }, `${kind === 'letter' ? 'enroute_letters' : 'shipments'}.xlsx`);
}

/** The row key the Letters sub-tab exports by (mirrors shipping-list.ts shippingRowKey, pinned in lockstep). */
export function shippingRowKey(r: ShippingRow): string {
  const ref = r.kind === 'kit' ? r.barcode_id : r.letter_id || r.tracking_code;
  return `${r.kind}|${ref}|${r.member_id}`;
}

export type LookupTab = 'member' | 'pcp' | 'enroute' | 'letters' | 'results';
export type LookupAuditAction = 'search' | 'filter' | 'view' | 'export';

/**
 * The Portal's audit beacon (POST /api/audit/ops-event there, /api/care/lookup-audit here): one row
 * per SETTLED search, per COMMITTED filter change, per open by link. Best-effort: never blocks the
 * screen and swallows every error; the server writes the row.
 */
export function lookupAudit(tab: LookupTab, action: LookupAuditAction, extra?: { query?: string; filters?: Record<string, string[]>; via?: string; memberId?: string }): void {
  try {
    void apiPost(`/api/care/lookup-audit`, { client: currentClient(), tab, action, ...(extra ?? {}) }).catch(() => undefined);
  } catch {
    /* a beacon never throws into the screen */
  }
}

export function createOutbound(memberId: string, consentState?: string): Promise<PostResult<{
  event: EventRecord;
}>> {
  const body: Record<string, unknown> = { client: currentClient(), memberId };
  if (consentState !== undefined) body.consentState = consentState;
  return apiPost(`/api/care/outbound/create`, body);
}

/** Live-call intake (Flow B): what Marny knows about the caller when she answers. */
export interface CallStartInput {
  callerType: 'member' | 'pcp' | 'other';
  phone?: string;
  /** The member calling (callerType 'member') or being discussed (callerType 'pcp'). */
  memberId?: string;
  pcpNpi?: string;
  pcpName?: string;
  note?: string;
  /** Per-form idempotency key: a retry after a 500 reuses the already-durable record server-side. */
  requestId?: string;
}

/** Mint the live inbound Voice event (claimed + in_progress); the console opens the drawer on it. */
export function startCall(input: CallStartInput): Promise<PostResult<{ event: EventRecord }>> {
  const body: Record<string, unknown> = { client: currentClient(), callerType: input.callerType };
  if (input.phone) body.phone = input.phone;
  if (input.memberId) body.memberId = input.memberId;
  if (input.pcpNpi) body.pcpNpi = input.pcpNpi;
  if (input.pcpName) body.pcpName = input.pcpName;
  if (input.note) body.note = input.note;
  if (input.requestId) body.requestId = input.requestId;
  return apiPost(`/api/care/call-start`, body);
}

/* ------------------------------------------------------------------------------------------------
 * SMS rail
 * ---------------------------------------------------------------------------------------------- */

/**
 * The member's whole SMS dialog, across every phone we know for them, each outbound message stamped
 * with the agent who sent it. Prefer memberId; phone is the fallback for an unmatched conversation.
 */
export async function getSmsConversation(
  args: { memberId?: string; phone?: string; campaign?: string; translate?: boolean },
): Promise<SmsConversationResp> {
  const q = new URLSearchParams({ client: currentClient() });
  if (args.memberId) q.set('memberId', args.memberId);
  if (args.phone) q.set('phone', args.phone);
  // Row 626: the entry's roster, so an A1c-KED member is found (not only 26 FOBT members).
  if (args.campaign) q.set('campaign', args.campaign);
  // Translate toggle: pull English `translatedText` for inbound non-English rows (defaults off).
  if (args.translate) q.set('translate', '1');
  return apiJson<SmsConversationResp>(`/api/care/sms/conversation?${q.toString()}`);
}

/**
 * The unread-text SIGNAL feed (badge poller). Threads the opaque cursor; PHI-minimized (no bodies).
 * `configured:false` means the rail is not wired - the poller stands down.
 */
export async function getSmsInbound(since: string): Promise<SmsInboundResp> {
  const q = new URLSearchParams({ client: currentClient() });
  if (since) q.set('since', since);
  return apiJson<SmsInboundResp>(`/api/care/sms/inbound?${q.toString()}`);
}

/**
 * Mint the idempotency key HERE, in the component that owns the compose box, and reuse it on retry.
 * A retry carrying the same id makes the platform replay its first result instead of sending a
 * second text. Minting a fresh id per attempt would silently double-text the member.
 */
export function newClientRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `cid-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Send one SMS. NOTE the return shape: a 200 with status 'suppressed' or 'quiet_hours' is a
 * successful CALL and an undelivered MESSAGE. Branch on `delivered`, never on `ok`.
 * A 502 carries status 'unknown': the message may or may not have gone. Retry the SAME
 * clientRequestId; do not mint a new one.
 */
export function sendSms(args: {
  text: string;
  clientRequestId: string;
  memberId?: string;
  phone?: string;
  /** Row 626: the entry's roster (the server looks the member up on it). */
  campaign?: string;
  contextEventId?: string;
}): Promise<PostResult<SmsSendResp>> {
  return apiPost(`/api/care/sms/send`, { client: currentClient(), ...args });
}

/**
 * The after-hours hold's list for one member's conversation: by the member, and by every number the drawer shows.
 * Asked only when the conversation answered smsHold (the switch on or test).
 */
export async function getHeldSms(args: { memberId?: string; phones: string[] }): Promise<HeldSmsListResp> {
  const q = new URLSearchParams({ client: currentClient() });
  if (args.memberId) q.set('memberId', args.memberId);
  const phones = args.phones.filter((p) => p);
  if (phones.length) q.set('phone', phones.join(','));
  return apiJson<HeldSmsListResp>(`/api/care/sms/held?${q.toString()}`);
}

/** Cancel one held text (only while it is held; a 409 names its state otherwise). */
export function cancelHeldSms(clientRequestId: string): Promise<PostResult<{ status: string; error?: string }>> {
  return apiPost(`/api/care/sms/held/cancel`, { client: currentClient(), clientRequestId });
}

/* ------------------------- M1 part B: the email reply (his R. 184, 4 Oct 2026) ------------------------- */


/**
 * The answer, on every status: `status` is the reply's outcome ('sent' | 'not-sent' | 'unknown' |
 * 'blocked' | 'audit-failed'), `message` or `error` is the sentence to show. A 429 is the volume brake
 * (keep the id: nothing reached Graph); a 502 with status 'unknown' means look in Sent Items, and the
 * same id will never send twice.
 */
export interface EmailReplyResp {
  status: string;
  replay?: boolean;
  row?: EmailReplyRow;
  message?: string;
  error?: string;
  code?: string;
  reason?: string;
  rule?: string;
  retry?: 'same-id';
  recordFailed?: boolean;
  sent?: boolean;
}

export function sendEmailReply(args: { id: string; text: string; clientRequestId: string }): Promise<PostResult<EmailReplyResp>> {
  return apiPost(`/api/care/event/email-reply`, { client: currentClient(), ...args });
}

/* ------------------------------ row 410: the Video (test) tab ------------------------------ */

/** One video as the API lists it: the picker's label, the PLATFORM's phrase for the text, the page link. */
export interface VideoTestVideo { test: string; label: string; phrase: string; language: string; url: string }
export interface VideoTestRecipientView { key: string; name: string; phoneDisplay: string }
export interface VideoTestSentRow {
  ts: string; timePacific: string; operator: string; recipientKey: string; recipientName: string;
  test: string; note: string; outcome: string; messageId: string; clientRequestId: string; link: string; replay: boolean;
}
export interface VideoTestConfigResp {
  client: string;
  mode: 'test' | 'on';
  noteMax: number;
  /** The base the links are built from (MEMBERCARE_VIDEO_PAGE_BASE_URL, the platform's own base). */
  pageBase: string;
  /** False when MEMBERCARE_CLARICONNECT_URL or MEMBERCARE_CLARICONNECT_SMS_SECRET is absent: Send answers 503. */
  platformConfigured: boolean;
  videos: VideoTestVideo[];
  recipients: VideoTestRecipientView[];
  /** The exact text per video for the note given on the query (none here: the tab composes live). */
  preview: Record<string, string>;
  noteError?: string;
  sentToday: VideoTestSentRow[];
  ledger: 'ok' | 'unavailable';
}
export interface VideoTestSendResp {
  /** The platform's outcome on a 200 (prefixed replay- on a replay); the console's stamp otherwise. */
  status: string;
  outcome?: string;
  /** True when the platform had this press already and answered its first outcome; nothing new went. */
  replay?: boolean;
  platformOutcome?: string;
  messageId?: string;
  link?: string;
  scheduledAt?: string;
  reason?: string;
  /** The sentence the banner prints, on every answer. */
  message: string;
  /** 'same-id' = nothing reached the platform or the outcome is unknown: keep the id. 'new-id' = mint one. */
  retry?: 'same-id' | 'new-id';
  clientRequestId?: string;
  ts?: string;
  ledger?: 'ok' | 'unavailable' | 'skipped';
  text?: string;
  rule?: string;
  sentAt?: string;
}

/** What the tab is built from. The API's 404 to an operator off the list surfaces as `API 404` here. */
export function videoTestConfig(): Promise<VideoTestConfigResp> {
  const q = new URLSearchParams({ client: currentClient() });
  return apiJson<VideoTestConfigResp>(`/api/care/video-test?${q.toString()}`);
}

/**
 * One press. The recipient is a KEY on the test list, never a number; the note is one line; the id
 * is minted by the tab (newClientRequestId) and KEPT when the answer says retry:'same-id', so a lost
 * answer or a brake refusal replays rather than sends twice.
 */
export function videoTestSend(args: {
  recipient: string;
  test: string;
  language: 'en';
  note: string;
  clientRequestId: string;
}): Promise<PostResult<VideoTestSendResp>> {
  return apiPost(`/api/care/video-test/send`, { client: currentClient(), ...args });
}

/* ---- Internal-numbers roster (feedback #17): CDX staff/test numbers, user-managed ---- */

export interface InternalNumberRow {
  phone: string;
  label: string;
  by: string;
  ts: string;
  /** true = pinned by MEMBERCARE_INTERNAL_NUMBERS (env); cannot be removed from the console. */
  readOnly: boolean;
}

export function getInternalNumbers(): Promise<{ client: string; count: number; numbers: InternalNumberRow[] }> {
  return apiJson(`/api/care/internal-numbers?client=${currentClient()}`);
}

export function addInternalNumber(phone: string, label?: string): Promise<PostResult<{
  client: string;
  entry: { phone: string; label: string; by: string; ts: string; active: boolean };
}>> {
  const body: Record<string, unknown> = { client: currentClient(), phone };
  if (label !== undefined) body.label = label;
  return apiPost(`/api/care/internal-numbers`, body);
}

export function removeInternalNumber(phone: string): Promise<PostResult<{ client: string; phone: string; removed: boolean }>> {
  return apiPost(`/api/care/internal-numbers`, { client: currentClient(), phone, remove: true });
}

// --- the STOP review (Part 2 piece 1, 2026-09-14) ---------------------------------------------

export interface OptOutReviewDecision {
  rowId: string;
  decision: 'approved' | 'rejected' | 'unclear';
  reason: string;
  by: string;
  byName: string;
  at: string;
  classAtDecision: string;
  memberId: string;
}

export interface OptOutReviewRow {
  rowId: string;
  memberId: string;
  memberName: string;
  phone: string;
  class: string;
  communication: string;
  channel: string;
  at: string;
  source: string;
  basis: string;
  campaign: string;
  enforcedToday: boolean | null;
  stationFlag: string;
  /** False when the cell is not the member speaking (a bare do-not-solicit timestamp, or nothing). */
  communicationIsMemberWords: boolean;
  communicationNote: string;
  decision: OptOutReviewDecision | null;
  /** The published basis's `source=` token, '' when none (his 226 cohort chips). */
  basisSource: string;
  campaignMarker: CampaignMarker;
}

export type CampaignMarker = 'on-current' | 'not-on-current' | 'unknown';

export interface OptOutReviewResp {
  rows: OptOutReviewRow[];
  total: number;
  pending: number;
  counts: Record<string, number>;
  /** The current campaign the marker was judged against, and the three-state counts over every reviewable row. */
  campaign?: string;
  markers?: Record<CampaignMarker, number>;
  rosterAvailable?: boolean;
  /** Per extract cohort (the basis `source=` token): rows, rows naming a member, and those still pending. */
  cohorts?: Record<string, { rows: number; withMember: number; withMemberPending: number }>;
  /** False while row 216's platform write is off: a "Not a stop" is recorded and queued, not sent. */
  platformWrite?: boolean;
  emptyReason?: string;
  /** Row 356: the sort applied, the slicer counts over every kept row, and what the slicers hide. */
  sort?: Array<{ k: string; asc: boolean }>;
  facets?: Record<string, Array<{ v: string; n: number }>>;
  sliceHidden?: number;
  record: {
    enabled: boolean; loaded: boolean; file: string; publishedAt: string;
    rows: number; lastError: string; lastAttemptAt: string;
  };
}

export async function optOutReview(params: {
  filter?: string; class?: string; q?: string; sort?: string; dir?: string;
  limit?: number; offset?: number; marker?: string; cohort?: string; withMember?: string;
  /** Row 356: `slicerParam` of the slicers set; the sort may be `k1:asc,k2:desc`. */
  slice?: string;
}): Promise<OptOutReviewResp> {
  const p = new URLSearchParams({ client: currentClient() });
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  return apiJson<OptOutReviewResp>(`/api/care/optout-review?${p.toString()}`);
}

export interface OptOutReversalRow {
  memberId: string; memberName: string; phone: string; campaign: string;
  kind: 'restart' | 'false-stop' | 'opt-in-only';
  stoppedAt: string; stopClass: string; stopChannel: string; stopCommunication: string;
  restartedAt: string; restartChannel: string; restartBasis: string; restartCommunication: string;
  gapSeconds: number | null; source: string; rowsLeftOffTheRecord: number | null;
  campaignMarker: CampaignMarker;
}

export interface OptOutReversalsResp {
  rows: OptOutReversalRow[];
  total: number;
  /** Rows whose published `kind` was not rendered as published; a statement about the file. */
  kindDisagreements?: number;
  emptyReason?: string;
  reversals: {
    enabled: boolean; campaign: string; loaded: boolean; file: string; publishedAt: string;
    rows: number; lastError: string; lastAttemptAt: string;
  };
}

/** The campaign's came-back list (GET /api/care/optout-reversals). Read-only; there is nothing to decide. */
export async function optOutReversals(q = ''): Promise<OptOutReversalsResp> {
  const p = new URLSearchParams({ client: currentClient() });
  if (q) p.set('q', q);
  return apiJson<OptOutReversalsResp>(`/api/care/optout-reversals?${p.toString()}`);
}

export async function optOutDecide(
  rowId: string,
  decision: 'approved' | 'rejected' | 'unclear',
  reason: string,
  clientRequestId?: string,
): Promise<{ ok: boolean; decision: OptOutReviewDecision; unblock?: { phone: string }; unblockDelivery: string; directWrite?: DirectWriteOutcome }> {
  return apiJson(`/api/care/optout-decide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client: currentClient(), rowId, decision, reason, ...(clientRequestId ? { clientRequestId } : {}) }),
  });
}

/* ------------------------------------------------------------------------------------------------
 * USPS rejections (row 218, nav 11). The table is GET; each decision is one row in the address
 * decisions record, written by the API, never a database write.
 * ---------------------------------------------------------------------------------------------- */
export interface PostalAddress { line1: string; line2: string; city: string; state: string; zip: string; zip4: string }
export type AddressDecisionKind = 'CLEARED' | 'CANNOT_BE_HANDLED';
export interface AddressDecisionRow {
  family: string; version: string; client: string; campaign: string; member_id: string;
  decision: AddressDecisionKind; address: PostalAddress | null;
  decided_by: string; decided_by_name: string; decided_at: string; basis: string; note: string; source: string;
}
export type RejectionKind = 'kit' | 'roster' | 'letter';
export interface UspsRejectionRow {
  rowId: string; kind: RejectionKind; ref: string; memberId: string; memberName: string; campaign: string;
  ours: PostalAddress; usps: PostalAddress | null; uspsReason: string; uspsPlace: string; uspsAt: string; sentAt: string;
  /** Row 265: USPS's own code for the failure; '' until the source captures one. */
  uspsCode: string;
  /** EasyPost's status_detail on a failing letter scan; a fallback shown as EasyPost's, never as USPS's. */
  carrierDetail?: string;
  decision: AddressDecisionRow | null;
}
export type RejectionState = 'open' | 'cleared' | 'cannot-be-handled' | 'all';
export interface UspsRejectionsResp {
  available: boolean; campaign: string; campaigns?: string[]; kinds: Record<RejectionKind, boolean>;
  counts: { open: number; cleared: number; cannotBeHandled: number };
  record: { filesRead: number; filesSkipped: number; site: string; error: string | null };
  state: RejectionState; total: number; count: number; rows: UspsRejectionRow[]; emptyReason?: string;
}

export async function uspsRejections(state: RejectionState, campaign = ''): Promise<UspsRejectionsResp> {
  const p = new URLSearchParams({ client: currentClient(), state });
  if (campaign) p.set('campaign', campaign);
  return apiJson<UspsRejectionsResp>(`/api/care/usps-rejections?${p.toString()}`);
}

export async function uspsRejectionDecide(
  rowId: string,
  decision: AddressDecisionKind,
  address: PostalAddress | null,
  note: string,
  campaign = '',
): Promise<PostResult<{ ok: boolean; decision: AddressDecisionRow; file: string }>> {
  return apiPost(`/api/care/usps-rejection-decide`, { client: currentClient(), rowId, decision, address, note, ...(campaign ? { campaign } : {}) });
}

/* ------------------------------------------------------------------------------------------------
 * The DO NOT CONTACT master table (row 286, nav 12). ClariConnect holds the table; the API reads it
 * through their contract and adds the campaign file's notation beside each row. REMOVE is the row
 * 216 unblock plus a CLEAR in the care decisions record; ADJUST changes no enforcement.
 * ---------------------------------------------------------------------------------------------- */
export type DncMethod = 'sms' | 'voice' | 'import' | 'care' | 'care-portal' | 'other';
export interface DncDialogLine { at: string; direction: 'out' | 'in' | 'carrier'; kind: string; templateKey: string; text: string }
export interface DncOurMessage { at: string; templateKey: string; text: string }
/** ctive is the MAIN table (on the list, not locked); locked is row 325's locked view. */
export type DncStatusFilter = 'active' | 'locked' | 'removed' | 'all';
export type DncSortKey = 'member' | 'phone' | 'when' | 'method' | 'status' | 'added';
/**
 * ROW 325: his four EXCEPTION lists, one nav tab each (10 Opt-outs, 11 USPS rejections, 12 Do not
 * contact, 13 Technical exceptions). The server validates the same closed set.
 */
export const DNC_KINDS = ['dnc', 'optout', 'delivery', 'technical'] as const;
export type DncKind = (typeof DNC_KINDS)[number];
/** The lists the case drawer's Mark as offers: an opt-out names one campaign and is marked on tab 10. */
export const DRAWER_MARK_KINDS = ['dnc', 'delivery', 'technical'] as const;
/** One of the client's current campaigns, from ClariConnect's list (row 327's selector). */
export interface DncCampaign { slug: string; label: string; isDefault: boolean }
export type DncKindFilter = DncKind | 'all';
export interface DncEvidence { file: string; row: string; check: string; detail: string }
export interface DncMember { memberExternalId: string; memberName: string; lastName: string; campaigns: string[] }
export interface DncHistoryEntry { at: string; by: string; verb: string; reason: string; auditId: string }
export interface DncFileNotation {
  memberId: string; onFile: boolean; doNotContact: boolean | null; optoutClass: string | null; optoutAt: string | null; doNotSolicit: string | null;
}
export interface DncRow {
  dncId: string; client: string; phone: string; memberExternalId: string | null; memberName: string | null;
  method: DncMethod; responseType: string | null; response: string | null; receivedAt: string | null; primaryAction: string;
  /** Approval 130: a seeded row (the platform's method `seed`) and the check's plain line. */
  seeded: boolean; reason: string | null;
  channel: 'both' | 'sms' | 'voice' | 'none'; enforced: boolean; addedAt: string; addedBy: string; source: string;
  active: boolean; removedAt: string | null; removedBy: string | null; removedReason: string | null;
  note: string | null; dialog: DncDialogLine[]; ourMessage: DncOurMessage | null; workbookRows: string[]; history: DncHistoryEntry[];
  kind: DncKind; issue: string | null; campaign: string | null;
  evidence: DncEvidence | null; alsoEvidence: DncEvidence[] | null; members: DncMember[] | null;
  locked: boolean; lockedBy: string | null; lockedAt: string | null; lockReason: string | null;
  rosterName: string | null; notation: DncFileNotation | null;
  careDecision: { decision: string; at: string; by: string } | null;
  /** Approval 177: an id the platform did not tie, shown and never written with; memberFrom says where it came from. */
  memberFrom?: string | null; membershipMemberId?: string | null; membershipMembers?: DncMember[];
  /** Approval 175: why the number is on the list, its source, date and the member's words. */
  basis?: string | null; basisSource?: string | null; basisDate?: string | null; basisWords?: string | null;
  /** Row 357: the address as the plan gave it and what the Post Office says (address rows on the page only). */
  postal?: PostalFactsView | null;
}
export interface DncListResp {
  platformWrite: boolean; dncMaster: boolean; rows: DncRow[]; total: number; counts: { active: number; locked?: number; removed: number };
  /** Per tab, counted within the STATUS on screen, so the two controls compose. */
  kindCounts?: Record<DncKind, number>;
  kinds?: readonly DncKind[];
  issueCounts?: Record<string, number>;
  /** Row 327: the campaigns to choose from, the one shown ('all' for every one), and what it hides. */
  campaigns?: DncCampaign[]; campaign?: string; totalAll?: number | null; outside?: number | null;
  asOf?: string; dropped?: number; rosterAvailable?: boolean; rosterUnavailable?: string; careDecisionsRead?: boolean;
  emptyReason?: string; source?: string;
  /** Row 355: this operator may open each row's stored Technical detail (the owner address only). */
  superAdmin?: boolean;
  /** Row 356: the sort applied, the slicer counts over the whole list, and what the slicers hide. */
  sort?: Array<{ k: string; asc: boolean }>;
  facets?: Record<string, Array<{ v: string; n: number }>>;
  sliceHidden?: number;
}

/** One selected row's own answer in a batch, and WHERE its outcome was decided. */
export interface DncBulkOutcome {
  dncId: string; memberId: string; status: 'done' | 'refused'; reason: string;
  decidedBy: 'console' | 'platform'; fileWritten?: boolean; fileError?: string;
  carrierStillOptedOut?: boolean | null; carrierCheck?: string; fileSkipped?: string; newDncId?: string;
}
export interface DncBulkResp {
  ok: boolean; op: string; kind?: DncKind; toKind?: DncKind; campaign?: string; locked?: boolean; batchId: string;
  summary: { selected: number; sent: number; done: number; refused: number };
  results: DncBulkOutcome[]; fileNote?: string; carrierNote?: string;
  /** Present only on a failure, and `replayable` says whether clicking again can possibly help. */
  platformError?: string; platformOutcome?: string; replayable?: boolean;
  note?: string; error?: string; status?: string;
}
export interface DncPlatformOutcome {
  available: boolean; attempted: boolean; reason: string; status?: 'done' | 'refused' | 'conflict' | 'invalid' | 'unknown';
  verb?: string; channel?: string; clientRequestId?: string; platformStatus?: string; carrierStillOptedOut?: boolean | null;
}
export interface DncRemoveResp {
  ok: boolean; status: string; reason?: string; dncId: string; memberId?: string | null; liftsClass?: string; fileNote?: string;
  directWrite?: { enabled: boolean; record: { written: boolean; file: string; error: string } | null; platform: DncPlatformOutcome; skipped: string };
  carrierStillOptedOut?: boolean | null; removedAt?: string | null; removedBy?: string | null; removedReason?: string | null;
}
export interface DncAdjustResp { ok: boolean; status: string; reason?: string; dncId: string; clientRequestId: string; changed?: unknown; note?: string }

export async function dncList(args: {
  status: DncStatusFilter; kind?: DncKindFilter; campaign?: string; issue?: string; q?: string;
  /** A single key (with `dir`), or row 356's `k1:asc,k2:desc` from `sortParam`. */
  sort?: DncSortKey | string; dir?: 'asc' | 'desc';
  /** Row 356: `slicerParam` of the slicers set. */
  slice?: string;
  limit?: number; offset?: number;
}): Promise<DncListResp> {
  const p = new URLSearchParams({ client: currentClient(), status: args.status });
  if (args.kind) p.set('kind', args.kind);
  if (args.campaign) p.set('campaign', args.campaign);
  if (args.issue) p.set('issue', args.issue);
  if (args.q) p.set('q', args.q);
  if (args.sort) p.set('sort', args.sort);
  if (args.dir) p.set('dir', args.dir);
  if (args.slice) p.set('slice', args.slice);
  if (args.limit) p.set('limit', String(args.limit));
  if (args.offset) p.set('offset', String(args.offset));
  return apiJson<DncListResp>(`/api/care/dnc?${p.toString()}`);
}

/** Row 578a (R. 128): POST /api/care/stop-restart's 200. `askMember` is the sentence the operator acts on. */
export interface StopRestartResp {
  ok: boolean; memberId: string; removed: string[]; notRemoved: Array<{ dncId: string; reason: string }>;
  file: string; carrierStillOptedOut: boolean | null; askMember: string; clientNote: string;
  error?: string; code?: string; status?: string; replayable?: boolean; clientRequestId?: string;
}
/** Row 578a: restart after STOP. Keep `clientRequestId` from a replayable failure and send it again. */
export async function stopRestart(memberId: string, reason: string, clientRequestId?: string): Promise<PostResult<StopRestartResp>> {
  return apiPost(`/api/care/stop-restart`, { client: currentClient(), memberId, reason, ...(clientRequestId ? { clientRequestId } : {}) });
}

export async function dncRemove(dncId: string, reason: string, clientRequestId?: string): Promise<PostResult<DncRemoveResp>> {
  return apiPost(`/api/care/dnc-remove`, { client: currentClient(), dncId, reason, ...(clientRequestId ? { clientRequestId } : {}) });
}

/**
 * The command bar. ONE reason for the batch and ONE clientRequestId, which is what makes a retry a
 * replay rather than a second write: keep the id from an answer that did not arrive and send it again.
 */
export async function dncBulk(args: {
  op: 'remove' | 'lock' | 'unlock' | 'mark' | 'move';
  dncIds?: string[];
  rows?: Array<{ phone?: string; memberExternalId?: string; memberName?: string; evidence?: DncEvidence }>;
  kind?: DncKind;
  /** move: the list the rows land on. */
  toKind?: DncKind;
  /** mark or move onto an opt-out: the ONE campaign it stops. */
  campaign?: string;
  lock?: boolean;
  reason: string;
  clientRequestId?: string;
}): Promise<PostResult<DncBulkResp>> {
  return apiPost(`/api/care/dnc-bulk`, { client: currentClient(), ...args });
}

export async function dncAdjust(
  dncId: string,
  reason: string,
  patch: { memberExternalId?: string | null; memberName?: string | null; note?: string },
  clientRequestId?: string,
): Promise<PostResult<DncAdjustResp>> {
  return apiPost(`/api/care/dnc-adjust`, { client: currentClient(), dncId, reason, patch, ...(clientRequestId ? { clientRequestId } : {}) });
}

/* ------------------ row 372: add a member to technical exclusion (tab 13) ------------------ */

/**
 * His four reasons and Other, in the server's closed set (api/src/lib/tech-exclusion.ts). A test
 * pins the two lists equal, so the picker can never offer a reason the server refuses.
 */
export const TECH_EXCLUSION_REASONS = ['no_longer_in_plan', 'incapacity', 'hospice', 'death', 'other'] as const;
export type TechExclusionReason = (typeof TECH_EXCLUSION_REASONS)[number];

/** One of the member's phones, and the other members who hold the same number. */
export interface TechExclusionPhoneView {
  phone: string;
  fields: string[];
  others: Array<{ memberId: string; fullName: string }>;
  otherCount: number;
  /** Another member also holds this number: the press blocks it TOO, and the operator must tick that she knows. */
  shared: boolean;
}
/** A shared phone as the plan names it: the number, how many others hold it, and who (capped). */
export interface TechExclusionSharedPhone {
  phone: string;
  otherCount: number;
  otherMemberIds: string[];
  others: Array<{ memberId: string; fullName: string }>;
}
/**
 * "Both rows" (William, 19 Sep 2026): the press writes the member-keyed hold AND a phone-keyed block
 * for every phone on the roster, shared phones included. The preview names all of it before the press.
 */
export interface TechExclusionPreviewResp {
  memberId: string;
  fullName: string;
  /** The year of birth only; '' when the roster carries none. */
  birthYear: string;
  phones: TechExclusionPhoneView[];
  /** The member row is always written. */
  memberRow: { memberId: string };
  shared: TechExclusionSharedPhone[];
  sharedMemberIds: string[];
  /** True when a phone is shared: the press needs sharedPhoneAck: true, which the tick sets. */
  ackRequired: boolean;
}
/** One row's outcome. `held` is already_listed: it was on the list before the press, so it stops exactly as asked. */
export interface TechExclusionRowOutcome {
  phone: string;
  memberKeyed: boolean;
  status: 'done' | 'held' | 'refused';
  reason: string;
  dncId: string;
  otherCount: number;
  others: Array<{ memberId: string; fullName: string }>;
}
export interface TechExclusionResp {
  ok: boolean;
  status: 'added' | 'partial' | 'refused' | 'unknown';
  memberId: string; fullName: string; reasonCode: TechExclusionReason; clientRequestId: string; locked: boolean;
  rows: TechExclusionRowOutcome[];
  shared: TechExclusionSharedPhone[];
  sharedMemberIds: string[];
  sharedPhoneAck: boolean;
  fileWritten: boolean; fileError?: string;
  platformError?: string; platformOutcome?: string; replayable?: boolean; note?: string;
}

/**
 * The preview, read by member id. A GET that does NOT throw on a refusal, because the screen has a
 * sentence for each (not on the roster, switched off, the database unreadable) and "API 404" is not one.
 */
export async function techExclusionPreview(memberId: string): Promise<PostResult<TechExclusionPreviewResp>> {
  const p = new URLSearchParams({ client: currentClient(), memberId });
  const res = await apiFetch(`/api/care/tech-exclusion-preview?${p.toString()}`);
  let data: PostResult<TechExclusionPreviewResp>['data'];
  try {
    data = (await res.json()) as PostResult<TechExclusionPreviewResp>['data'];
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}

/**
 * The addition. The member is named by ID ONLY: the server re-reads the phones and the name from the
 * roster and refuses a body that carries either. `sharedPhoneAck` is sent as true ONLY when the
 * operator ticked the shared-phone line under a confirm that named the other members; the server
 * refuses a press on a shared phone without it. Keep the clientRequestId from an unknown outcome and
 * send it again: the platform replays the first answer and writes nothing twice.
 */
export async function techExclusionAdd(args: {
  memberId: string;
  reasonCode: TechExclusionReason;
  words?: string;
  sharedPhoneAck?: boolean;
  clientRequestId?: string;
}): Promise<PostResult<TechExclusionResp>> {
  return apiPost(`/api/care/tech-exclusion`, {
    client: currentClient(),
    memberId: args.memberId,
    reasonCode: args.reasonCode,
    ...(args.words ? { words: args.words } : {}),
    ...(args.sharedPhoneAck === true ? { sharedPhoneAck: true } : {}),
    ...(args.clientRequestId ? { clientRequestId: args.clientRequestId } : {}),
  });
}

/* ------------------------------ row 391: the reply buttons ------------------------------ */

/** One entry's answer to a press (mirrors api/src/care/reply-actions.ts RowOutcome). */
export interface ReplyRowOutcome {
  eventId: string;
  memberId: string;
  replyClass: string;
  campaign: string | null;
  /**
   * done: the entry is in the Archive. refused: nothing was written. error: see the reason.
   * staged (wave 3 item 4): the press was QUEUED on the case and takes effect when the case is approved.
   */
  status: ReplyActionOutcome;
  /** Wave 3 item 4: the contract's own word for a queued row (`outcome: 'staged'`); read beside `status`. */
  outcome?: ReplyActionOutcome;
  code: string;
  reason: string;
  decidedBy: 'console' | 'platform' | 'storage' | '';
  archived: boolean;
  file?: string;
  kitRequest?: 'approved' | 'created';
  /** Row 400 rev2 (opt-in): the /1.1 envelope's path, the map's kit id, and who approved. */
  envelope?: string;
  kitCode?: string;
  approvedBy?: { email: string; initials: string; initialsSource?: 'typed' | 'account' };
  /** Row 400 rev2 (refer): the new main-queue entry this one became. */
  referredEventId?: string;
  platform?: { attempted: boolean; status: 'held' | 'refused' | 'failed' | 'not-sent'; reason: string; replayable?: boolean };
  /** Approval 187: the later refusal an Opt In was refused for (code later_stop, later_decline, do_not_text). */
  laterRefusal?: LaterRefusalFlag;
  /** Approval 187 (d): the member said 2, then 1 again. Never blocks. */
  answerChanged?: AnswerChangedFlag;
}

export interface ReplyActionsResp {
  ok: boolean;
  op: 'opt-in' | 'opt-out' | 'stop' | 'refer';
  words: string;
  /** `staged` (wave 3 item 4) counts the entries whose press was queued for approval, when the server says. */
  summary: { selected: number; done: number; refused: number; error: number; staged?: number };
  results: ReplyRowOutcome[];
  batchId: string;
  /** Row 400 rev2: true once an Opt In press dropped its /1.1 envelope for at least one entry. */
  resolutionEmitted: boolean;
  kitRequestNote?: string;
  platformNote?: string;
  /** Row 400 rev2 (refer): what a Send to Main Dock did, and whether the reply history could be read. */
  referralNote?: string;
  historyChecked?: boolean;
  platform?: { enabled: boolean; clientRequestId: string; replayable: boolean; note?: string };
  /** PL-009 K4: present when the decisions went to the master's door; `replayable` = some entry's answer was unknown. */
  master?: { enabled: boolean; clientRequestId: string; replayable: boolean; note?: string };
  /** Approval 187 (opt-in only): whether the press could read the other replies and the do-not-text list. */
  laterRefusalCheck?: { repliesRead: boolean; doNotTextChecked: boolean; note?: string };
  /**
   * Wave 3 item 4: true when the server took the press as one to QUEUE for approval (the deployment
   * stages presses and the press was not an approval press). Nothing took effect and nothing was sent.
   */
  staged?: boolean;
  stagedNote?: string;
  error?: string;
  code?: string;
}

/**
 * Wave 3 item 4: what an approval did with one press that was queued on a case (mirrors
 * api/src/lib/staged-apply.ts AppliedPress). `eventId` rides the batch approve's lines only.
 */
export interface AppliedPressAnswer {
  eventId?: string;
  pendingId: string;
  kind: PendingPress['kind'];
  op: string;
  applied: boolean;
  /** Applied: what the press's own code answered (done, held, closed-no-action). */
  outcome?: string;
  /** Refused: the press's own sentence. */
  reason?: string;
  pressedBy: string;
  pressedAt: string;
}

/**
 * The request id a retry must send again, or '' when nothing is to be retried: the platform's half (Opt
 * Out, Stop) or the master's door (every op with MEMBERCARE_MASTER_DECISIONS on) answered UNKNOWN for some
 * entry. Both halves carry the press's one request id, so either answer names the same id, and a press
 * sent again with it is answered from what the platform or the door already holds, never written twice.
 */
/**
 * PL-009 K4 (Care's 1:03 PM point 2): one pass of the master read-back for the selected client. The shell
 * calls it every two minutes while presses go to the master's door; the answer is counts and entry ids.
 */
export function masterReadback(): Promise<PostResult<Record<string, unknown>>> {
  return apiPost(`/api/care/master-readback`, { client: currentClient() });
}

export function replayIdOf(data: Pick<ReplyActionsResp, 'platform' | 'master'>): string {
  if (data.platform?.replayable && data.platform.clientRequestId) return data.platform.clientRequestId;
  if (data.master?.replayable && data.master.clientRequestId) return data.master.clientRequestId;
  return '';
}

/**
 * One press of the reply buttons. Keep `clientRequestId` from an answer whose platform half was
 * UNKNOWN and send it again to retry that press safely; the platform replays and writes nothing twice.
 * `reason` rides a refer press only (row 400 rev2): one reason for the whole press.
 *
 * `approval` (wave 3 item 4) says THIS PRESS IS THE APPROVAL, so it acts at once instead of being
 * queued for one. Only the 01A grid's Approve, Approve opt out and Approve STOP pass it, and only
 * while the deployment stages presses; Send to Main Dock, the Inbound queue's bar and the case's own
 * reply presses never do.
 */
/**
 * 16 Oct 2026, the Inbound queue's buttons on top (his word: "all of the items checked will go to the
 * approval lane (another tab) for the action the keys say"): the same press, sent with `toApproval:
 * true`, so it is QUEUED on each case for Final Review whatever the deployment's staging switch says.
 * The server runs every refusal first and queues nothing the approval could not carry out.
 */
export function replyActionsToApproval(
  op: 'opt-in' | 'opt-out' | 'stop',
  ids: string[],
  clientRequestId: string,
): Promise<PostResult<ReplyActionsResp>> {
  return apiPost(`/api/care/reply-actions`, { client: currentClient(), op, ids, clientRequestId, toApproval: true });
}

export function replyActions(
  op: 'opt-in' | 'opt-out' | 'stop' | 'refer',
  ids: string[],
  clientRequestId?: string,
  reason?: string,
  initials?: string,
  approval?: boolean,
): Promise<PostResult<ReplyActionsResp>> {
  return apiPost(`/api/care/reply-actions`, {
    client: currentClient(), op, ids,
    ...(clientRequestId ? { clientRequestId } : {}),
    ...(reason ? { reason } : {}),
    // Row 521: the operator's typed initials, when they typed some. Omitted entirely otherwise, so a
    // press from any other caller is byte for byte the press this route took before row 521.
    ...(initials ? { initials } : {}),
    // Wave 3 item 4: exactly `true`, and only when asked for. Omitted entirely otherwise, so every
    // other press is byte for byte the press this route took before the approval contract.
    ...(approval === true ? { approval: true } : {}),
  });
}
